import {
  alpha,
  Box,
  Card,
  CardContent,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Save as SaveIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import {
  callAPI,
  customValidation,
  Method,
  updateMainHeader,
  validations,
} from "../../commonFunctions/CommonFunctions";
import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import WSTextField, { TextType } from "../../Components/TextBox/WSTextBox";
import WSButton from "../../Components/Button/WSButton";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";

export const User: React.FC<any> = React.memo((props) => {
  const { control, register, handleSubmit, setValue } = useForm();
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const params = useParams();
  const id = params?.id;

  const { showMessage } = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      const fetchData = async () => {
        try {
          const response = await callAPI(
            `Vyapar/User/${id}`,
            Method.GET
          );

          if (response && response.data) {
            const userData = response.data;
            setValue("fullName", userData.userName);
            setValue("email", userData.email);
          }
        } catch (error) {
          console.error("Failed to fetch user data:", error);
        }
      };
      fetchData();
    }
  }, [id, setValue]);

  const handleData = async (data: any) => {
    try {
      setLoading(true);

      const request = {
        userName: data.fullName,
        email: data.email,
        passwordHash: data.password,
        roleId: "767E561C-0BA2-4584-BBE6-D4890363AC06",
      };

      const response = await callAPI(
        "Vyapar/User",
        Method.POST,
        request
      );

      if (response) {
        showMessage(
          response?.message,
          response.success === true ? "success" : "error"
        );

        if (response.success === true) {
          navigate("/user/list");
        }
      }
    } catch (error) {
      console.error("Failed to save user:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    navigate("/user/list");
  };

  useEffect(() => {
    updateMainHeader("User");
  });

  breadCrumbsStore.updateBreadCrumbs([
    { "User List": "/user/list" },
    { [!id ? "Add User" : "Update User"]: "/add-user" },
  ]);

  return (
    <Box sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
      <form onSubmit={handleSubmit(handleData)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: "blur(8px)",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PersonIcon
                    sx={{ color: theme.palette.primary.main, fontSize: 28 }}
                  />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {!id ? "Add User" : "Update User"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Enter the primary details of the user
                  </Typography>
                </Box>
              </Box>

              <Stack direction="row" spacing={2}>
                <WSButton
                  id="cancelBtn"
                  variant="outlined"
                  color="error"
                  size="small"
                  label="Cancel"
                  onClick={handleCancelClick}
                />

                <WSButton
                  id="saveBtn"
                  variant="contained"
                  color="primary"
                  size="small"
                  type="submit"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                  label={loading ? "Saving..." : "Save"}
                />
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                height: "100%",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Enter User Details
                </Typography>

                <Grid container spacing={3} sx={{ mt: 0 }}>
                  <Grid item xs={12} md={6}>
                    <WSTextField
                      label="Enter Full Name"
                      {...register("fullName")}
                      id="fullName"
                      name="fullName"
                      control={control}
                      rules={{ required: "Full name is required" }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <WSTextField
                      id="email"
                      type={TextType.Text}
                      rules={customValidation(true, validations.email)}
                      {...register("email")}
                      label="Email"
                      control={control}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <WSTextField
                      id="password"
                      type={TextType.Password}
                      {...register("password")}
                      label="Password"
                      control={control}
                      rules={{ required: "Password is required" }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
});
